import { useMemo } from 'react';
import { ConsentBankState, ConsentPartner, useConsentBanks } from '@ibk/contabilidade-digital';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { Product, productApi, selectSelectedAccountId, useAppSelector } from '@ibk/shared-config';
import { formatCurrency } from '@ibk/shared-utils';
export function useOtherBanksBalance() {
    const showBalance = useAppSelector(state => state.showBalance.showBalance);
    const selectedAccountIdFromRedux = useAppSelector(selectSelectedAccountId);
    const partyId = useMemo(() => {
        if (selectedAccountIdFromRedux) {
            return selectedAccountIdFromRedux;
        }
        const accountInfo = getSelectedAccountInfo();
        return accountInfo?.id || '';
    }, [selectedAccountIdFromRedux]);
    const { data, isLoading } = productApi.useGetProductQuery({
        product: Product.CONTABILIDADE_DIGITAL,
        selectedCompanyPartyId: partyId,
        debounceMs: 300,
    }, {
        skip: !partyId,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: false,
        refetchOnReconnect: true,
    });
    const { consentBanks, isLoading: isLoadingBanks } = useConsentBanks(partyId, {
        refetchOnMountOrArgChange: true,
        skip: !partyId,
        refetchOnFocus: false,
        refetchOnReconnect: true,
        prioritizePluggy: true,
        onlyPluggy: true,
    });
    const balance = useMemo(() => {
        if (!data || typeof data !== 'object' || !('result' in data)) {
            return 0;
        }
        const metadata = data.result?.metadata;
        return metadata?.balance ?? 0;
    }, [data]);
    const connectedPluggyBanks = useMemo(() => {
        if (!consentBanks || consentBanks.length === 0) {
            return [];
        }
        return consentBanks.filter(bank => bank.partner === ConsentPartner.PLUGGY &&
            (bank.state === ConsentBankState.CONNECTED || bank.state === ConsentBankState.VALID));
    }, [consentBanks]);
    const bankNames = useMemo(() => {
        if (connectedPluggyBanks.length === 0) {
            return [];
        }
        const uniqueBanks = new Map();
        connectedPluggyBanks.forEach(bank => {
            const bankCode = bank.externalBankCode;
            const bankName = bank.externalInstitutionDescription || '';
            if (!bankCode || !bankName) {
                return;
            }
            const isNumericCode = /^\d+$/.test(bankCode);
            const key = isNumericCode ? bankCode : bankName.toUpperCase();
            if (!uniqueBanks.has(key)) {
                uniqueBanks.set(key, {
                    name: bankName,
                    code: isNumericCode ? bankCode : '',
                });
            }
        });
        return Array.from(uniqueBanks.values());
    }, [connectedPluggyBanks]);
    const hasConnectedBanks = useMemo(() => {
        return connectedPluggyBanks.length > 0;
    }, [connectedPluggyBanks]);
    const formattedBalance = useMemo(() => {
        return formatCurrency(balance);
    }, [balance]);
    const getBalanceValue = () => {
        if (!showBalance)
            return '*****';
        if (isLoading || isLoadingBanks)
            return undefined;
        return formattedBalance;
    };
    return {
        balance,
        formattedBalance,
        isLoading: isLoading || isLoadingBanks,
        bankNames,
        getBalanceValue,
        hasConnectedBanks,
        consentBanks,
    };
}
